// My actions' Unread answers tab: conversations holding a reply the operator has not
// opened since, then their messages still waiting on one. See
// docs/Y2026-Q4/design-unread-answers.md.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  type UnreadConversation,
  type UnreadAnswers as UnreadAnswersData,
  type WaitingState,
  useSetAllRead,
  useSetRunRead,
} from "../api/hooks/useUnreadAnswers";
import { statefulRunPage } from "../routes/learningAgentPaths";
import { formatWhen } from "../utils/formatWhen";
import { agentsLabel } from "../utils/runParticipants";
import { MultiSelectFilter } from "./MultiSelectFilter";
import styles from "./UnreadAnswers.module.css";

const PROJECT_NOUN = { one: "project", many: "projects" };
// What the project filter calls a conversation about no one project.
const NO_PROJECT = "All projects";

type Order = "newest" | "oldest";

const projectOf = (row: UnreadConversation): string =>
  row.project_name || (row.context_project_id ? "" : NO_PROJECT);

/** When a row last moved: its latest unread reply, or the message still waiting. */
const timeOf = (row: UnreadConversation): number =>
  Date.parse(row.reply?.at ?? row.sent.at);

const SENT_PREFIX: Record<UnreadConversation["sent"]["kind"], string> = {
  message: "",
  answer: "Your answer: ",
  comment: "Your comment: ",
};

const WAITING: Record<WaitingState, string> = {
  working: "Working on it",
  queued: "Queued behind a turn already running",
  stalled: "No reply, and nothing is running. Open it and send again.",
};

// One set of widths for both tables, so the Still working rows line up with the ones above.
const Columns = () => (
  <colgroup>
    <col className={styles.whereCol} />
    <col />
    <col className={styles.replyCol} />
    <col className={styles.actionCol} />
  </colgroup>
);

const Where = ({ row }: { row: UnreadConversation }) => (
  <td>
    <Link className={styles.title} to={statefulRunPage(row)}>
      {row.title || "Untitled conversation"}
    </Link>
    <span className={styles.project}>{projectOf(row)}</span>
  </td>
);

const Sent = ({ row }: { row: UnreadConversation }) => (
  <td>
    <span className={styles.quote} title={row.sent.text}>
      {SENT_PREFIX[row.sent.kind]}
      {row.sent.text}
    </span>
  </td>
);

const UnreadRow = ({
  row,
  email,
}: {
  row: UnreadConversation;
  email: string;
}) => {
  const setRead = useSetRunRead(email);
  const reply = row.reply;
  return (
    <tr>
      <Where row={row} />
      <Sent row={row} />
      <td>
        {reply && (
          <>
            <strong>{agentsLabel(reply.agent, reply.participants)}</strong>
            <span className={styles.when}>
              {formatWhen(reply.at)}
              {reply.count > 1 && ` · ${reply.count} replies`}
            </span>
          </>
        )}
      </td>
      <td className={styles.action}>
        <button
          type="button"
          className={styles.markRead}
          disabled={setRead.isLoading}
          onClick={() =>
            setRead.mutate({
              runId: row.run_id,
              read: true,
              throughSeq: reply?.through_seq,
            })
          }
        >
          Mark read
        </button>
        {setRead.error && (
          <span className={styles.error}>
            Could not mark it read ({setRead.error.message})
          </span>
        )}
      </td>
    </tr>
  );
};

// Marks every row shown (the project filter narrows it), after a confirm, so one stray
// click cannot clear the list.
const MarkAllRead = ({
  rows,
  email,
}: {
  rows: UnreadConversation[];
  email: string;
}) => {
  const [confirming, setConfirming] = useState(false);
  const setAll = useSetAllRead(email);
  const items = rows.flatMap((row) =>
    row.reply ? [{ runId: row.run_id, throughSeq: row.reply.through_seq }] : [],
  );
  if (items.length === 0) return null;
  return (
    <span className={styles.markAll}>
      {confirming ? (
        <>
          <span className={styles.markAllAsk}>
            Mark {items.length === 1 ? "it" : `all ${items.length}`} as read?
          </span>
          <button
            type="button"
            className={`${styles.markRead} ${styles.markReadPrimary}`}
            disabled={setAll.isLoading}
            onClick={() =>
              setAll.mutate(items, { onSuccess: () => setConfirming(false) })
            }
          >
            {setAll.isLoading ? "Marking…" : `Mark ${items.length} read`}
          </button>
          <button
            type="button"
            className={styles.markRead}
            disabled={setAll.isLoading}
            onClick={() => {
              setAll.reset();
              setConfirming(false);
            }}
          >
            Cancel
          </button>
        </>
      ) : (
        <button
          type="button"
          className={styles.markRead}
          onClick={() => setConfirming(true)}
        >
          Mark all as read
        </button>
      )}
      {setAll.error && (
        <span className={styles.error}>
          Could not mark them read ({setAll.error.message})
        </span>
      )}
    </span>
  );
};

const WaitingRow = ({ row }: { row: UnreadConversation }) => (
  <tr className={styles.waiting}>
    <Where row={row} />
    <Sent row={row} />
    <td>
      <span className={row.state === "stalled" ? styles.stalled : ""}>
        {row.state && WAITING[row.state]}
      </span>
      <span className={styles.when}>sent {formatWhen(row.sent.at)}</span>
    </td>
    <td className={styles.action} />
  </tr>
);

export const UnreadAnswers = ({
  data,
  error,
  isLoading,
  email,
}: {
  data: UnreadAnswersData | undefined;
  error: Error | null;
  isLoading: boolean;
  email: string;
}) => {
  const [projects, setProjects] = useState<string[]>([]);
  const [order, setOrder] = useState<Order>("newest");
  const all = useMemo(
    () => [...(data?.unread ?? []), ...(data?.working ?? [])],
    [data],
  );
  const options = useMemo(
    () => [...new Set(all.map(projectOf).filter(Boolean))].sort(),
    [all],
  );
  const shown = (rows: UnreadConversation[]) =>
    rows
      .filter((r) => !projects.length || projects.includes(projectOf(r)))
      .sort((a, b) =>
        order === "newest" ? timeOf(b) - timeOf(a) : timeOf(a) - timeOf(b),
      );

  if (isLoading) return <p className={styles.muted}>Loading your answers…</p>;
  if (error || !data)
    return (
      <p className={styles.error}>
        Could not load your unread answers
        {error ? ` (${error.message})` : ""}. Reload to try again.
      </p>
    );
  const unread = shown(data.unread);
  const working = shown(data.working);
  return (
    <div className={styles.panel}>
      {all.length > 0 && (
        <div className={styles.filters} role="group" aria-label="Show">
          <MultiSelectFilter
            noun={PROJECT_NOUN}
            size="compact"
            options={options}
            selected={projects}
            onChange={setProjects}
          />
          {projects.length > 0 && (
            <button
              type="button"
              className={styles.clear}
              onClick={() => setProjects([])}
            >
              Clear filters
            </button>
          )}
          <span className={styles.count}>
            Showing {unread.length} of {data.unread.length} unread
          </span>
          <label className={styles.control}>
            Sort
            <select
              value={order}
              onChange={(e) => setOrder(e.target.value as Order)}
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
          </label>
          <MarkAllRead
            // A different set of rows asks again, so a confirm never outlives what it counted.
            key={unread.map((row) => row.run_id).join(",")}
            rows={unread}
            email={email}
          />
        </div>
      )}
      <div className={styles.card}>
        {unread.length === 0 ? (
          <p className={styles.caughtUp}>
            {data.unread.length === 0
              ? "You're all caught up."
              : "No unread answers on these projects."}
          </p>
        ) : (
          <table className={styles.table}>
            <Columns />
            <thead>
              <tr>
                <th>Conversation</th>
                <th>You sent</th>
                <th>Reply</th>
                <th aria-label="Mark read" />
              </tr>
            </thead>
            <tbody>
              {unread.map((row) => (
                <UnreadRow key={row.run_id} row={row} email={email} />
              ))}
            </tbody>
          </table>
        )}
        {working.length > 0 && (
          <section>
            <h3 className={styles.group}>Still working ({working.length})</h3>
            <table className={styles.table}>
              <Columns />
              <tbody>
                {working.map((row) => (
                  <WaitingRow key={row.run_id} row={row} />
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </div>
  );
};
